import {
    Component,
    EventEmitter,
    HostListener,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { GruppeTier } from '../classes/gruppe-tier';
import { GruppeTierBemerkung } from '../classes/gruppe-tier';
import { Gruppe, GruppenStatistik } from '../../gruppen/classes/gruppe';
import { HttpParams } from '@angular/common/http';
import { debounceTime, distinctUntilChanged, Subscription } from 'rxjs';
import { API_BASE_URL_SERVICE } from '../../../kiene-core.config';
import { MessageService } from '../../../services/message-service/message.service';
import { TierFilter } from '../filter/gruppe-tier-filter-dialog/classes/tier-filter';
import { TierFilterSettings } from '../filter/gruppe-tier-filter-dialog/classes/tier-filter-settings';
import { TierFilterSettingsPersistence } from '../filter/gruppe-tier-filter-dialog/classes/tier-filter-settings-persistence';
import { TierSortierFilter } from '../classes/tier-sortier-filter';
import { UntypedFormControl } from '@angular/forms';
import { KieneUtils } from '../../../utils/KieneUtils';
import { MatDialog } from '@angular/material/dialog';
import { GruppeTierHilfeDialogComponent } from '../gruppe-tier-hilfe-dialog/gruppe-tier-hilfe-dialog.component';
import { KieneBackendApiService } from '../../../services/backend-api/kiene-backend-api.service';
import { ActivatedRoute } from '@angular/router';
import { GruppeTierFilterButtonComponent } from '../../gruppe-tier/filter/gruppe-tier-filter-button/gruppe-tier-filter-button.component';
import { GruppeTierService } from '../gruppe-tier.service';
import { EnterValueDialogComponent } from '../../dialogs/enter-value-dialog/enter-value-dialog.component';
import { EnterValueInput, EnterValueResponse } from '../../dialogs/enter-value-dialog/enter-value-response';
import { AusstallenService } from '../../ausstallen/ausstallen.service';
import { GruppeTierHaendlerAendernDialogComponent } from '../gruppe-tier-haendler-aendern-dialog/gruppe-tier-haendler-aendern-dialog.component';
import { GruppeTierKontaktSetzenDialogComponent } from '../gruppe-tier-kontakt-setzen-dialog/gruppe-tier-kontakt-setzen-dialog.component';
import { GruppeTierHerkunftSetzenDialogComponent } from '../gruppe-tier-herkunft-setzen-dialog/gruppe-tier-herkunft-setzen-dialog.component';
import { GruppeTierReklamationenSetzenDialogComponent } from '../gruppe-tier-reklamationen-setzen-dialog/gruppe-tier-reklamationen-setzen-dialog.component';
import { GruppeTierPreiseFestlegenDialogComponent } from '../gruppe-tier-preise-festlegen-dialog/gruppe-tier-preise-festlegen-dialog.component';
import { GruppeTierGewichteFestlegenDialogComponent } from '../gruppe-tier-gewichte-festlegen-dialog/gruppe-tier-gewichte-festlegen-dialog.component';
import { LocalStorageService } from '../../../services/local-storage/local-storage.service';

@Component({
    selector: 'kiene-gruppe-tier-list',
    templateUrl: './gruppe-tier-list.component.html',
    styleUrls: ['./gruppe-tier-list.component.scss'],
})
export class GruppeTierListComponent implements OnInit, OnDestroy, OnChanges {
    @ViewChild('filterButton') filterButton: GruppeTierFilterButtonComponent;
    @Input('gruppeTiereUrl') gruppeTiereUrl = 'gruppe_tier/read_kundenportal.php';
    @Input('gruppeUrl') gruppeUrl = 'gruppe/read_one_kundenportal.php';
    @Input('gruppeStatistikUrl') gruppeStatistikUrl = 'gruppe_tier/read_statistik.php';
    @Input('gruppeId') gruppeId: number;

    // Optionen
    @Input('hideSuche') hideSuche = false;
    @Input('hideFilter') hideFilter = false;
    @Input('hideSortierung') hideSortierung = false;
    @Input('hideHilfeButton') hideHilfeButton = false;
    @Input('hideReloadButton') hideReloadButton = false;
    @Input('watchMode') watchMode = false;
    @Input('enableMobileSelection') enableMobileSelection = false;
    @Input('hideInlineSelectAllButton') hideInlineSelectAllButton = false;
    /** Load/save tier filter via speicher (group-details only). */
    @Input() persistFilterToSpeicher = false;

    @Output('reload') reload = new EventEmitter<boolean>();
    @Output('filterSelected') filterSelected = new EventEmitter<TierFilterSettings>();
    @Output() filterSaveEnabledChange = new EventEmitter<boolean>();
    @Output('selectedTiereChange') selectedTiereChange = new EventEmitter<GruppeTier[]>();

    gruppe: Gruppe;
    gruppeTiere: GruppeTier[];

    isLoading = true;
    public tierFilter = TierFilter;
    filterSettings: TierFilterSettings = new TierFilterSettings();
    filterSaveEnabled = false;
    ueberschrift: string;
    selectedSortierFilter: TierSortierFilter;
    selectedAbteil: string;
    selectedTierFilter: TierFilter = TierFilter.ALLE_TIERE;
    gruppenStatistik: GruppenStatistik;
    isSelectionMode = false;
    private selectedTierIds = new Set<number>();
    private selectedTiereById = new Map<number, GruppeTier>();
    private longPressTimer: ReturnType<typeof setTimeout> | null = null;
    private longPressTriggeredForTierId: number | null = null;
    private readonly longPressThresholdMs = 550;

    // Formcontrols
    searchCtrl = new UntypedFormControl();
    scanUnvollstaendigCtrl = new UntypedFormControl();

    // Subscriptions
    searchSub: Subscription;
    tierfilterSubscription: Subscription;
    gruppenStatistikSubscription: Subscription;
    gruppeSubscription: Subscription;

    private persistedFilterLoaded = false;

    constructor(
        private api: KieneBackendApiService,
        @Inject(API_BASE_URL_SERVICE) private baseUrl: string,
        private messageService: MessageService,
        private dialog: MatDialog,
        private route: ActivatedRoute,
        private gruppeTierService: GruppeTierService,
        private ausstallenService: AusstallenService,
        private localStorageService: LocalStorageService,
    ) {}

    ngOnChanges(): void {
        if (this.gruppeId) {
            this.gruppe = new Gruppe(); // solange noch nicht geladen
            this.gruppe.gruppe_id = this.gruppeId;
            this.loadGruppe();
        }
    }

    ngOnDestroy(): void {
        this.searchSub?.unsubscribe();
        this.gruppeSubscription?.unsubscribe();
        this.tierfilterSubscription?.unsubscribe();
        this.clearLongPressTimer();
    }

    ngOnInit(): void {
        const search = this.route.snapshot.queryParamMap.get('search');
        this.searchCtrl.setValue(search, { emitEvent: false });

        if (this.gruppeId) {
            this.gruppe = new Gruppe(); // solange noch nicht geladen
            this.gruppe.gruppe_id = this.gruppeId;
            this.loadGruppe();
        }

        this.searchCtrl.valueChanges.pipe(distinctUntilChanged(), debounceTime(300)).subscribe({
            next: () => {
                this.loadTiere();
            },
        });

        this.scanUnvollstaendigCtrl.valueChanges.subscribe({
            next: value => {
                this.loadTiere({ scanUnvollstaendig: value });
            },
        });
    }

    loadGruppe() {
        if (this.persistFilterToSpeicher && !this.persistedFilterLoaded) {
            this.persistedFilterLoaded = true;
            TierFilterSettingsPersistence.load(this.api).subscribe({
                next: state => {
                    if (state?.saveFilterEnabled && state.settings?.isFilterActive()) {
                        this.filterSettings = state.settings;
                        this.filterSelected.emit(this.filterSettings);
                    }
                    this.filterSaveEnabled = state?.saveFilterEnabled ?? false;
                    this.filterSaveEnabledChange.emit(this.filterSaveEnabled);
                    this.loadGruppeData();
                },
                error: () => this.loadGruppeData(),
            });
            return;
        }
        this.loadGruppeData();
    }

    private loadGruppeData() {
        this.loadGruppenStatistik();

        const params = new HttpParams().set('gruppe_id', this.gruppe?.gruppe_id?.toString());

        this.gruppeSubscription?.unsubscribe();
        this.gruppeSubscription = this.api.get(this.baseUrl + this.gruppeUrl, params).subscribe({
            next: gruppe => {
                this.gruppe = gruppe;
                this.ueberschrift = `${Number(this.gruppe.historie) > 0 ? 'Historie: ' : ''} ${this.gruppe.bezeichnung}`;
                this.loadTiere();
            },
            error: err => {
                this.messageService.errorMessage('Fehler!', err);
            },
        });
    }

    loadGruppenStatistik() {
        const params = new HttpParams().set('gruppe_id', this.gruppe?.gruppe_id?.toString());

        this.gruppenStatistikSubscription?.unsubscribe();
        this.gruppenStatistikSubscription = this.api.get(this.baseUrl + this.gruppeStatistikUrl, params).subscribe({
            next: gruppenStatistik => {
                this.gruppenStatistik = gruppenStatistik;
            },
            error: err => {
                this.messageService.errorMessage('Fehler!', err);
            },
        });
    }

    loadTiere(options?: { scanUnvollstaendig?: boolean }) {
        this.gruppeTiere = [];

        let params = this.filterSettings.getAsParams();
        console.log('Filter Params: %s', JSON.stringify(params));

        if (this.selectedSortierFilter?.param) {
            const keys: string[] = this.selectedSortierFilter.param.keys();
            for (const k of keys) {
                const values: string[] = this.selectedSortierFilter.param.getAll(k);
                for (const v of values) {
                    params = params.append(k, v);
                }
            }
        } else {
            params = params.set('orderby', 'geburtsdatum asc, tier_id asc');
        }

        params = params.append('gruppe_id', this.gruppe?.gruppe_id?.toString());

        if (this.searchCtrl.value) {
            params = params.append('search', this.searchCtrl.value);
        }

        if (options && options.scanUnvollstaendig) {
            params = params.set('scan_fehlt', options.scanUnvollstaendig ? '1' : '0');
        }

        this.isLoading = true;
        this.searchSub?.unsubscribe();
        this.searchSub = this.api.get(this.baseUrl + this.gruppeTiereUrl, params).subscribe({
            next: response => {
                this.isLoading = false;
                this.gruppeTiere = response.records;
                this.reconcileSelectionWithVisibleRows();
            },
            error: error => {
                this.isLoading = false;
                this.messageService.errorMessage('Fehler beim Laden der Tiere!', error);
            },
        });
    }

    validFilterSettings(filterSettings: TierFilterSettings) {
        return (
            filterSettings?.filter !== TierFilter.ALLE_TIERE ||
            KieneUtils.isNNoU(filterSettings?.rassen) ||
            KieneUtils.isNNoU(filterSettings?.reklamationen) ||
            KieneUtils.isNNoU(filterSettings?.geschlechter) ||
            KieneUtils.isNNoU(filterSettings?.abteile)
        );
    }

    sortiereTiere(sortierung?: TierSortierFilter) {
        this.isLoading = true;
        this.selectedSortierFilter = sortierung;
        this.loadTiere();
    }

    setFilterSettings(filterSettings: TierFilterSettings) {
        this.filterSettings = filterSettings;
        this.loadTiere();
        this.filterSelected.emit(filterSettings);
    }

    openHilfe() {
        this.dialog.open(GruppeTierHilfeDialogComponent, { minWidth: '90vw', width: '90vw' });
    }

    reloadEvent() {
        this.loadTiere();
        this.reload.emit(true);
    }

    onTierTouchStart(tier: GruppeTier): void {
        if (!this.isMobileSelectionActive()) {
            return;
        }

        this.clearLongPressTimer();
        this.longPressTriggeredForTierId = null;

        this.longPressTimer = setTimeout(() => {
            this.isSelectionMode = true;
            this.selectTier(tier);
            this.longPressTriggeredForTierId = tier?.gt_id ?? null;
        }, this.longPressThresholdMs);
    }

    onTierTouchEnd(): void {
        this.clearLongPressTimer();
    }

    onTierTouchMove(): void {
        this.clearLongPressTimer();
    }

    onTierTouchCancel(): void {
        this.clearLongPressTimer();
    }

    onTierMouseDown(tier: GruppeTier): void {
        this.onTierTouchStart(tier);
    }

    onTierMouseUp(): void {
        this.onTierTouchEnd();
    }

    onTierMouseLeave(): void {
        this.onTierTouchCancel();
    }

    onTierContextMenu(event: MouseEvent): void {
        if (!this.isMobileSelectionActive()) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
    }

    @HostListener('document:contextmenu', ['$event'])
    onDocumentContextMenu(event: MouseEvent): void {
        if (!this.isMobileSelectionActive()) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
    }

    onTierClick(tier: GruppeTier, event: MouseEvent): void {
        if (!this.isMobileSelectionActive() || !this.isSelectionMode) {
            return;
        }

        if (this.longPressTriggeredForTierId === tier?.gt_id) {
            event.preventDefault();
            event.stopPropagation();
            this.longPressTriggeredForTierId = null;
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        this.toggleTierSelection(tier);
    }

    isTierSelected(tier: GruppeTier): boolean {
        return this.selectedTierIds.has(tier?.gt_id);
    }

    get selectedCount(): number {
        return this.selectedTierIds.size;
    }

    selectAllVisibleTiere(): void {
        for (const tier of this.gruppeTiere ?? []) {
            this.selectedTierIds.add(tier.gt_id);
            this.selectedTiereById.set(tier.gt_id, tier);
        }
        this.isSelectionMode = this.selectedTierIds.size > 0;
        this.emitSelectedTiereChange();
    }

    toggleSelectAllVisibleTiere(): void {
        if (this.areAllVisibleTiereSelected()) {
            this.discardSelection();
            return;
        }

        this.selectAllVisibleTiere();
    }

    areAllVisibleTiereSelected(): boolean {
        const visibleTiere = this.gruppeTiere ?? [];
        if (visibleTiere.length === 0) {
            return false;
        }

        return visibleTiere.every(tier => this.selectedTierIds.has(tier.gt_id));
    }

    discardSelection(): void {
        this.selectedTierIds.clear();
        this.selectedTiereById.clear();
        this.isSelectionMode = false;
        this.emitSelectedTiereChange();
    }

    markSelectedAsScanned(): void {
        const selectedTiere = this.getSelectedTiere();
        if (selectedTiere.length === 0) {
            return;
        }

        this.gruppeTierService.ohrmarkeAlsGescanntMarkieren(this.gruppe?.gruppe_id, selectedTiere).subscribe({
            next: result => {
                if (result) {
                    this.reloadEvent();
                    this.discardSelection();
                }
            },
        });
    }

    addCommentToSelected(): void {
        const selectedTiere = this.getSelectedTiere();
        if (selectedTiere.length === 0) {
            return;
        }

        const input = new EnterValueInput();
        input.title = 'Bemerkung hinzufügen';
        input.text = `${selectedTiere.length} selektierte Tiere erhalten die gleiche Bemerkung.`;
        input.okButton = 'Speichern';
        input.buttonColor = 'accent';
        input.addInputValue('Bemerkung', 'text');

        const dialogRef = this.dialog.open(EnterValueDialogComponent, { data: input, width: '90vw', maxWidth: '520px' });
        dialogRef.afterClosed().subscribe({
            next: (result: EnterValueResponse) => {
                const text = result?.getValue(0);
                if (!result?.result || !text) {
                    return;
                }

                const gtm = new GruppeTierBemerkung();
                for (const row of selectedTiere) {
                    gtm.gt_ids.push(row.gt_id);
                }
                gtm.bemerkung = text;

                this.api.post(this.baseUrl + 'gruppe_tier_bemerkung/update_kundenportal_bemerkungen.php', gtm, undefined).subscribe({
                    next: () => {
                        this.messageService.infoMessage('Bemerkung wurde hinzugefügt!');
                        this.reloadEvent();
                    },
                    error: err => {
                        this.messageService.errorMessage('Fehler!', err);
                    },
                });
            },
        });
    }

    changeGroupForSelected(): void {
        const selectedTiere = this.getSelectedTiere();
        if (selectedTiere.length === 0) {
            return;
        }

        this.gruppeTierService.openGruppeAuswaehlenDialog(selectedTiere).subscribe({
            next: changed => {
                if (changed) {
                    this.reloadEvent();
                    this.discardSelection();
                }
            },
        });
    }

    changeAbteilForSelected(): void {
        const selectedTiere = this.getSelectedTiere();
        if (selectedTiere.length === 0) {
            return;
        }

        const input = new EnterValueInput();
        input.title = 'Abteil ändern';
        input.text = `${selectedTiere.length} selektierte Tiere werden aktualisiert.`;
        input.okButton = 'Ändern';
        input.buttonColor = 'accent';
        input.addInputValue('Neues Abteil', 'text');

        const dialogRef = this.dialog.open(EnterValueDialogComponent, { data: input, width: '90vw', maxWidth: '520px' });
        dialogRef.afterClosed().subscribe({
            next: (result: EnterValueResponse) => {
                const abteil = result?.getValue(0)?.toString()?.trim();
                if (!result?.result || !abteil) {
                    return;
                }

                this.api
                    .post(this.baseUrl + 'gruppe_tier/update_abteil.php', {
                        abteil,
                        tiere: selectedTiere,
                    })
                    .subscribe({
                    next: () => {
                        this.messageService.infoMessage('Abteil wurde für selektierte Tiere geändert.');
                        this.reloadEvent();
                        this.discardSelection();
                    },
                    error: err => {
                        this.messageService.errorMessage('Fehler beim Ändern des Abteils!', err);
                    },
                });
            },
        });
    }

    changeHaendlerForSelected(): void {
        const selectedTiere = this.getSelectedTiere();
        if (selectedTiere.length === 0) {
            return;
        }

        const dialogRef = this.dialog.open(GruppeTierHaendlerAendernDialogComponent, {
            width: '95vw',
            maxWidth: '620px',
            data: { anzahlTiere: selectedTiere.length, tiere: selectedTiere },
        });

        dialogRef.afterClosed().subscribe({
            next: (result: boolean | undefined) => {
                if (!result) {
                    return;
                }
                this.messageService.infoMessage('Händler wurde für selektierte Tiere geändert.');
                this.reloadEvent();
                this.discardSelection();
            },
        });
    }

    openKontaktSetzenDialog(): void {
        const selectedTiere = this.getSelectedTiere();
        if (selectedTiere.length === 0) {
            return;
        }

        this.dialog
            .open(GruppeTierKontaktSetzenDialogComponent, {
                width: '98vw',
                maxWidth: '920px',
                panelClass: 'kontakt-setzen-dialog-panel',
                data: { tiere: selectedTiere, gruppeId: this.gruppe?.gruppe_id ?? this.gruppeId },
            })
            .afterClosed()
            .subscribe({
                next: (result: boolean | undefined) => {
                    if (!result) {
                        return;
                    }
                    this.reloadEvent();
                    this.discardSelection();
                },
            });
    }

    openHerkunftSetzenDialog(): void {
        const selectedTiere = this.getSelectedTiere();
        if (selectedTiere.length === 0) {
            return;
        }

        this.dialog
            .open(GruppeTierHerkunftSetzenDialogComponent, {
                width: '98vw',
                maxWidth: '920px',
                panelClass: 'herkunft-setzen-dialog-panel',
                data: { tiere: selectedTiere },
            })
            .afterClosed()
            .subscribe({
                next: (result: boolean | undefined) => {
                    if (!result) {
                        return;
                    }
                    this.reloadEvent();
                    this.discardSelection();
                },
            });
    }

    openReklamationenSetzenDialog(): void {
        const selectedTiere = this.getSelectedTiere();
        if (selectedTiere.length === 0) {
            return;
        }

        this.dialog
            .open(GruppeTierReklamationenSetzenDialogComponent, {
                width: '95vw',
                maxWidth: '700px',
                data: { tiere: selectedTiere, gruppeId: this.gruppe?.gruppe_id ?? this.gruppeId },
            })
            .afterClosed()
            .subscribe({
                next: (result: boolean | undefined) => {
                    if (!result) {
                        return;
                    }
                    this.reloadEvent();
                    this.discardSelection();
                },
            });
    }

    changeEinstalldatumForSelected(): void {
        this.openDateDialogForSelected('Einstalldatum ändern', 'Neues Einstalldatum', newDate =>
            this.gruppeTierService.einstalldatumHitAendern(this.getSelectedTiere(), newDate),
        );
    }

    changeAbgangsdatumForSelected(): void {
        this.openDateDialogForSelected('Abgangsdatum ändern', 'Neues Abgangsdatum', newDate =>
            this.gruppeTierService.abgangsdatumHitAendern(this.getSelectedTiere(), newDate),
        );
    }

    setAussendienstForSelected(): void {
        const selectedTiere = this.getSelectedTiere();
        if (selectedTiere.length === 0) {
            return;
        }

        this.gruppeTierService.aussendienstSetzen(selectedTiere).subscribe({
            next: success => {
                if (success) {
                    this.reloadEvent();
                    this.discardSelection();
                }
            },
        });
    }

    openPreiseFestlegenDialog(): void {
        const selectedTiere = this.getSelectedTiere();
        if (selectedTiere.length === 0) {
            return;
        }

        this.dialog
            .open(GruppeTierPreiseFestlegenDialogComponent, {
                width: '500px',
                maxWidth: '95vw',
                data: { gruppeTiere: selectedTiere },
            })
            .afterClosed()
            .subscribe({
                next: (result: boolean | undefined) => {
                    if (!result) {
                        return;
                    }
                    this.reloadEvent();
                    this.discardSelection();
                },
            });
    }

    openGewichteFestlegenDialog(): void {
        const selectedTiere = this.getSelectedTiere();
        if (selectedTiere.length === 0) {
            return;
        }

        this.dialog
            .open(GruppeTierGewichteFestlegenDialogComponent, {
                width: '500px',
                maxWidth: '95vw',
                data: { gruppeId: this.gruppe?.gruppe_id, gruppeTiere: selectedTiere },
            })
            .afterClosed()
            .subscribe({
                next: (result: boolean | undefined) => {
                    if (!result) {
                        return;
                    }
                    this.reloadEvent();
                    this.discardSelection();
                },
            });
    }

    ausstallenSelected(): void {
        const selectedTiere = this.getSelectedTiere();
        if (selectedTiere.length === 0) {
            return;
        }

        this.ausstallenService.ausstallen(this.gruppe, true, selectedTiere);
    }

    isMobileSelectionActive(): boolean {
        if (!this.enableMobileSelection) {
            return false;
        }

        if (typeof window === 'undefined') {
            return false;
        }

        const isNarrowViewport = window.matchMedia('(max-width: 1200px)').matches;
        const hasCoarsePointer = window.matchMedia('(pointer: coarse)').matches;
        const hasTouch = typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0;

        return isNarrowViewport || hasCoarsePointer || hasTouch;
    }

    canChangeGewichte(): boolean {
        return this.localStorageService.hasPermission(9161);
    }

    private openDateDialogForSelected(
        title: string,
        label: string,
        apiCall: (newDate: Date) => any,
    ): void {
        if (this.getSelectedTiere().length === 0) {
            return;
        }

        const input = new EnterValueInput();
        input.title = title;
        input.text = `${this.getSelectedTiere().length} selektierte Tiere werden aktualisiert.`;
        input.okButton = 'Ändern';
        input.buttonColor = 'accent';
        input.addInputValue(label, 'date');

        const dialogRef = this.dialog.open(EnterValueDialogComponent, { data: input });
        dialogRef.afterClosed().subscribe({
            next: (result: EnterValueResponse) => {
                if (result?.result && result?.getValue(0)) {
                    const dateValue: Date = result.getValue(0);
                    apiCall(dateValue).subscribe({
                        next: () => {
                            this.messageService.infoMessage('Änderung erfolgreich gespeichert.');
                            this.reloadEvent();
                            this.discardSelection();
                        },
                        error: err => {
                            this.messageService.errorMessage('Fehler!', err);
                        },
                    });
                }
            },
        });
    }

    private toggleTierSelection(tier: GruppeTier): void {
        if (this.isTierSelected(tier)) {
            this.selectedTierIds.delete(tier.gt_id);
            this.selectedTiereById.delete(tier.gt_id);
        } else {
            this.selectTier(tier);
        }

        if (this.selectedTierIds.size === 0) {
            this.isSelectionMode = false;
        }
        this.emitSelectedTiereChange();
    }

    private selectTier(tier: GruppeTier): void {
        if (tier?.gt_id) {
            this.selectedTierIds.add(tier.gt_id);
            this.selectedTiereById.set(tier.gt_id, tier);
        }
    }

    private getSelectedTiere(): GruppeTier[] {
        const selectedTiere = Array.from(this.selectedTiereById.values());
        if (selectedTiere.length > 0) {
            return selectedTiere;
        }

        // Fallback: keep selection actionable even when rows are no longer visible.
        return Array.from(this.selectedTierIds.values()).map(gt_id => ({ gt_id } as GruppeTier));
    }

    private reconcileSelectionWithVisibleRows(): void {
        if (!this.selectedTierIds.size || !this.gruppeTiere?.length) {
            return;
        }

        for (const tier of this.gruppeTiere) {
            if (this.selectedTierIds.has(tier.gt_id)) {
                this.selectedTiereById.set(tier.gt_id, tier);
            }
        }

        this.emitSelectedTiereChange();
    }

    private emitSelectedTiereChange(): void {
        this.selectedTiereChange.emit(this.getSelectedTiere());
    }

    private clearLongPressTimer(): void {
        if (this.longPressTimer) {
            clearTimeout(this.longPressTimer);
            this.longPressTimer = null;
        }
    }
}
